<script setup lang="ts">
import { useTemplateRef } from 'vue';
import { animations } from '@/services/Animations';
import type {
  UploadedPhotoItemHandle,
  UploadedSidebarPhoto,
} from './useUploadedPhotosSidebar';

const props = defineProps<{
  hasAnimated: boolean;
  isAnimating: boolean;
  photo: UploadedSidebarPhoto;
}>();

const outer = useTemplateRef<HTMLElement>('outer');
const inner = useTemplateRef<HTMLElement>('inner');

function animateFrom(fromRef?: HTMLElement | object): Promise<void> {
  if (!(outer.value && inner.value)) {
    return Promise.resolve();
  }
  return animations.appearArc([outer.value, inner.value], {
    duration: 500,
    fromRef,
  }).promise;
}

defineExpose<UploadedPhotoItemHandle>({
  animateFrom,
  get photoUuid(): string {
    return props.photo.uuid;
  },
});
</script>

<template>
  <li
    ref="outer"
    class="UploadedPhotosSidebar__animation-outer"
    data-testid="uploaded-photo-item"
  >
    <div ref="inner" class="UploadedPhotosSidebar__animation-inner">
      <div
        class="UploadedPhotosSidebar__photo"
        :class="{
          'UploadedPhotosSidebar__photo--animating': isAnimating,
          'UploadedPhotosSidebar__photo--animated': hasAnimated,
        }"
      >
        <i
          class="UploadedPhotosSidebar__photo-icon kiosk-success-icon"
        />
        <img
          class="UploadedPhotosSidebar__photo-image sentry-block"
          :src="photo.url ?? undefined"
        >
      </div>
    </div>
  </li>
</template>

<style scoped lang="scss">
.UploadedPhotosSidebar__animation-outer {
  padding: 0;
  margin: 0;
}

.UploadedPhotosSidebar__photo {
  width: 8.2rem;
  height: 6.1rem;
  position: relative;
  margin-top: 1.7rem;
  opacity: 0;

  &--animating,
  &--animated {
    opacity: 1;
  }
}

.UploadedPhotosSidebar__photo-icon {
  --kiosk-success-icon-size: 1.8rem;

  position: absolute;
  top: -0.6rem;
  left: -0.7rem;
  z-index: 1;
}

.UploadedPhotosSidebar__photo-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0.4rem;
  box-shadow:
    0 0 0 0.18rem var(--Global--success-color),
    0 0.4rem 0.4rem rgba(var(--Kiosk--tint-color-rgb), 0.4);
}
</style>
